import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Checkbox
} from '@mui/material';
import ScrollToTop from '../common/ScrollToTop';
import api from '../../config/axios';
import templateLanguageService from '../../services/templateLanguage.service';
import { templateLanguageUpdatedBase } from '../../data/templateLanguageUpdatedBase';
import { normalizeTemplateLanguagePlainText } from '../../utils/normalizeTemplateLanguagePlainText';
import {
  REGULATORY_TEMPLATE_LANGUAGE_TITLES,
  TEMPLATE_LANGUAGE_BASE_TITLE_ALIASES,
} from '../../constants/templateLanguageFieldRows';

const TEMPLATES = [
  { id: 'default', name: 'TransCelerate_Version 11' },
];

const TRANSCELERATE_TEMPLATE_ID = 'default';

function isTranscelerateTemplate(templateId) {
  return templateId === TRANSCELERATE_TEMPLATE_ID;
}

function resolveTemplateLanguageBaseText(title) {
  const key = String(title ?? '').trim();
  if (!key) return '';
  if (templateLanguageUpdatedBase[key]) return templateLanguageUpdatedBase[key];
  const alias = TEMPLATE_LANGUAGE_BASE_TITLE_ALIASES[key];
  if (alias && templateLanguageUpdatedBase[alias]) return templateLanguageUpdatedBase[alias];
  return '';
}

const INITIAL_TITLES = [
  'Confidentiality Statement',
  '5.4.1 Definitions Related to Childbearing Potential',
  '5.4.2: Contraception Requirements',
  '7.1 Permanent discontinuation of Trial Intervention',
  '7.1.2 Temporary Discontinuation of Trial Intervention',
  '7.1.3 Rechallenge',
  '7.2 Participant Discontinuation or Withdrawal from Trial',
  '7.3 : Management of Loss to Follow-Up',
  '9. ADVERSE EVENTS, SERIOUS ADVERSE EVENTS, PRODUCT COMPLAINTS, PREGNANCY AND POSTPARTUM INFORMATION, AND SPECIAL SAFETY SITUATIONS',
  '9.1 Definitions',
  '9.1.1 Definition of Adverse event',
  '9.1.2 Definition of Serious Adverse event',
  '9.1.3 Definitions of Product Complaints',
  '9.2 Timing and Procedures for Collection and Reporting',
  '9.2.1 Timing',
  '9.2.2 Collection procedure',
  '9.2.3 Reporting',
  '9.2.3.1 Regulatory Reporting Requirements',
  '9.2.4 Adverse events of special interest',
  '9.2.5 Disease-related Events or Outcomes Not Qualifying as AEs or SAEs',
  '9.3 Pregnancy and Postpartum Information',
  '9.3.1 Participants Who Become Pregnant During the Trial',
  '9.3.2 Participants Whose Partners Become Pregnant During the Trial',
  '9.4 Special Safety situations',
  ...REGULATORY_TEMPLATE_LANGUAGE_TITLES,
];

function normalizeTitleKey(title) {
  return String(title ?? '')
    .trim()
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ');
}

function buildEmptyGrid() {
  return INITIAL_TITLES.map((title) => ({
    title,
    selected: true,
    content: '',
  }));
}

/** Hydrate grid from Mongo `tableData` (saved Submit), keyed by title. */
function mergeSavedRowsIntoGrid(savedTableData) {
  const rows = Array.isArray(savedTableData) ? savedTableData : [];
  const exact = new Map();
  const norm = new Map();
  rows.forEach((r) => {
    if (!r || typeof r !== 'object') return;
    const t = r.title;
    if (t == null || String(t).trim() === '') return;
    exact.set(String(t), r);
    norm.set(normalizeTitleKey(t), r);
  });
  return INITIAL_TITLES.map((title) => {
    const hit = exact.get(title) ?? norm.get(normalizeTitleKey(title));
    return {
      title,
      selected: hit ? !!hit.selected : true,
      content: hit?.content != null ? String(hit.content) : '',
    };
  });
}

/** Fill empty cells from authoritative baseline text (same for all studies). */
function hydrateFromBaseText(rows) {
  return rows.map((row) => {
    const existing = String(row.content ?? '').trim();
    if (existing) return row;
    const base = resolveTemplateLanguageBaseText(row.title);
    return {
      ...row,
      content: base ? normalizeTemplateLanguagePlainText(base) : '',
    };
  });
}

const TemplateLanguageForm = ({ onSubmit }) => {
  const { user } = useAuth();
  const isAdminUser = user?.role === 'admin';

  const [selectedTemplate, setSelectedTemplate] = useState(TRANSCELERATE_TEMPLATE_ID);
  const [tableData, setTableData] = useState(buildEmptyGrid);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hydratingSaved, setHydratingSaved] = useState(false);
  const [loadedFromSaved, setLoadedFromSaved] = useState(false);

  const loadTranscelerateTemplateLanguage = useCallback(async () => {
    setFetching(true);
    setHydratingSaved(true);
    setError(null);
    try {
      const latest = await templateLanguageService.getLatestTemplateLanguageByTemplate(
        TRANSCELERATE_TEMPLATE_ID
      );
      if (latest && Array.isArray(latest.tableData) && latest.tableData.length > 0) {
        setTableData(hydrateFromBaseText(mergeSavedRowsIntoGrid(latest.tableData)));
        setLoadedFromSaved(true);
      } else {
        setTableData(hydrateFromBaseText(buildEmptyGrid()));
        setLoadedFromSaved(false);
      }
    } catch (e) {
      console.warn('[TemplateLanguageForm] Could not load TransCelerate template language:', e);
      setTableData(hydrateFromBaseText(buildEmptyGrid()));
      setLoadedFromSaved(false);
      setError(
        'Could not load saved TransCelerate template language. Showing standard baseline text where available.'
      );
    } finally {
      setFetching(false);
      setHydratingSaved(false);
    }
  }, []);

  useEffect(() => {
    if (isTranscelerateTemplate(selectedTemplate)) {
      loadTranscelerateTemplateLanguage();
    }
  }, [selectedTemplate, loadTranscelerateTemplateLanguage]);

  const allSelected = useMemo(() => tableData.every((r) => r.selected), [tableData]);
  const someSelected = useMemo(() => tableData.some((r) => r.selected), [tableData]);

  const setAllSelected = (checked) => {
    setTableData((prev) => prev.map((r) => ({ ...r, selected: checked })));
  };

  const toggleRowSelected = (index, checked) => {
    setTableData((prev) => prev.map((r, i) => (i === index ? { ...r, selected: checked } : r)));
  };

  const handleTemplateChange = (e) => {
    const template = e.target.value;
    setSelectedTemplate(template);
    if (!isTranscelerateTemplate(template)) {
      setTableData(buildEmptyGrid());
      setLoadedFromSaved(false);
    }
  };

  const isTranscelerateSelected = isTranscelerateTemplate(selectedTemplate);

  const handleContentChange = (index, value) => {
    if (!isAdminUser) return;
    setTableData((prev) =>
      prev.map((row, i) => (i === index ? { ...row, content: value } : row))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const formattedTableData = tableData.map((row) => ({
        title: row.title,
        selected: !!row.selected,
        content: row.content || '',
      }));

      const formData = {
        template: selectedTemplate,
        tableData: formattedTableData,
      };

      const response = await api.post('/template-language/submit', formData);

      if (response.data.success) {
        setSuccess(true);
        setLoadedFromSaved(true);
        setTimeout(() => {
          if (onSubmit) {
            onSubmit(response.data.data);
          }
          if (window.setActiveTab) {
            window.setActiveTab((prev) => prev + 1);
          }
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to save form data. Please try again.');
      }
    } catch (err) {
      console.error('Error saving form:', err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Failed to save form data. Please check your connection and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Template Language
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Selecting <strong>TransCelerate_Version 11</strong> loads the organization-wide template
        language shared by <strong>all projects and studies</strong> (latest saved TransCelerate
        record, with standard baseline text for any empty section). No per-study AI generation runs
        on this page.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Select Template</InputLabel>
            <Select
              value={selectedTemplate}
              onChange={handleTemplateChange}
              label="Select Template"
            >
              {TEMPLATES.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          {isTranscelerateSelected && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1 }}>
              <Button
                type="button"
                variant="outlined"
                color="primary"
                onClick={loadTranscelerateTemplateLanguage}
                disabled={fetching || loading || hydratingSaved}
              >
                {fetching ? 'Loading…' : 'Reload TransCelerate template language'}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
                Fetches the latest saved <strong>TransCelerate</strong> template language (same for
                every study). Empty rows use the standard baseline library.
              </Typography>
            </Box>
          )}
          {isTranscelerateSelected && loadedFromSaved && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Showing saved organization TransCelerate template language.
            </Typography>
          )}
          {!isTranscelerateSelected && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Select <strong>TransCelerate_Version 11</strong> to load shared template language for
              all studies.
            </Alert>
          )}
          {!isAdminUser && isTranscelerateSelected && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Section text is read-only. Only administrators can edit template language on this page.
            </Alert>
          )}
        </Grid>

        <Grid item xs={12}>
          {hydratingSaved && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Loading template language…
              </Typography>
              <CircularProgress size={20} sx={{ ml: 1, verticalAlign: 'middle' }} />
            </Box>
          )}
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table
              size="small"
              sx={{
                tableLayout: 'fixed',
                width: '100%',
                '& .MuiTableCell-root': { verticalAlign: 'top' },
              }}
            >
              <colgroup>
                <col style={{ width: 52 }} />
                <col style={{ width: '27%' }} />
                <col />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ px: 0.5, py: 1 }}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={(e) => setAllSelected(e.target.checked)}
                      disabled={loading || hydratingSaved || fetching}
                      inputProps={{ 'aria-label': 'Select all template language titles' }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1, pr: 1, fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ py: 1, pl: 0.5 }}>Content</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell padding="checkbox" sx={{ px: 0.5, py: 1 }}>
                      <Checkbox
                        checked={row.selected}
                        onChange={(e) => toggleRowSelected(index, e.target.checked)}
                        disabled={loading || hydratingSaved || fetching}
                        size="small"
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1,
                        pr: 1,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        whiteSpace: 'normal',
                      }}
                    >
                      <Typography variant="body2" component="div">
                        {row.title}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1, pl: 0.5, width: '100%' }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={isAdminUser ? 4 : 3}
                        value={row.content}
                        onChange={(e) => handleContentChange(index, e.target.value)}
                        InputProps={{
                          readOnly: !isAdminUser,
                          sx: {
                            alignItems: 'flex-start',
                          },
                        }}
                        placeholder={isAdminUser ? 'Edit section text…' : 'Loaded template language'}
                        variant="outlined"
                        size="small"
                        sx={{
                          m: 0,
                          '& .MuiInputBase-root': { py: 0.5 },
                          '& .MuiInputBase-input': {
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            fontFamily: 'inherit',
                          },
                          ...(isAdminUser
                            ? {}
                            : {
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'action.hover',
                                },
                              }),
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              sx={{ minWidth: 120 }}
              disabled={loading || hydratingSaved || fetching || !isTranscelerateSelected}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Template language saved successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={error !== null}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <ScrollToTop />
    </Box>
  );
};

export default TemplateLanguageForm;
